import React from "react";
import HeaderLabelWithTooltip from "../../components/Organismos/Table/HeaderLabelWithTooltip";
import type { FirmContactValidation } from "../../utils/firmContactUtils";

/** Matches EventAttendeesDashboard section headings. */
export const SECTION_TITLE_CLASS =
  "mb-4 text-lg font-medium leading-none text-gray-font font-decimal-medium";

/** Matches EventAttendeesDashboard fee / label rows. */
export const FEE_TYPE_LABEL_CLASS =
  "font-sans text-sm font-normal leading-5 tracking-normal text-gray-font";

/** Read-only field values — same weight/color family as dashboard metric values. */
export const FIELD_VALUE_CLASS =
  "mt-1 text-base font-semibold leading-5 text-gray-font md:text-lg";

export const SUMMARY_VALUE_CLASS =
  "text-base font-semibold tabular-nums leading-none text-gray-font md:text-lg";

export const ACCOUNTING_PANEL_CLASS =
  "flex h-full min-w-0 flex-col rounded border border-gray-200 bg-white p-4 font-sans md:p-6";

export const ACCOUNTING_INSET_PANEL_CLASS =
  "rounded border border-gray-200 bg-accent-Default-light p-4";

type AccountingPanelProps = {
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
};

export function AccountingPanel({
  title,
  children,
  footer,
}: AccountingPanelProps): React.ReactElement {
  return (
    <div className={ACCOUNTING_PANEL_CLASS}>
      <h2 className={SECTION_TITLE_CLASS}>{title}</h2>
      <div className="flex flex-1 flex-col">{children}</div>
      {footer ? <div className="mt-6 pt-2">{footer}</div> : null}
    </div>
  );
}

type AccountingDetailFieldProps = {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
};

export function AccountingDetailField({
  label,
  value,
  valueClassName = FIELD_VALUE_CLASS,
}: AccountingDetailFieldProps): React.ReactElement {
  return (
    <div className="min-w-0">
      <div className={FEE_TYPE_LABEL_CLASS}>{label}</div>
      <div className={valueClassName}>{value}</div>
    </div>
  );
}

const ACCOUNTING_SUMMARY_LABEL_TOOLTIP_CLASS =
  "pointer-events-none fixed z-[9999] max-w-xs -translate-x-1/2 whitespace-normal rounded bg-gray-font px-3 py-2 text-left text-xs text-neutral shadow-lg";

type AccountingSummaryRowProps = {
  label: string;
  value: string;
  labelTooltip?: string;
};

export function AccountingSummaryRow({
  label,
  value,
  labelTooltip,
}: AccountingSummaryRowProps): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <HeaderLabelWithTooltip
        label={label}
        tooltip={labelTooltip}
        className={FEE_TYPE_LABEL_CLASS}
        tooltipClassName={ACCOUNTING_SUMMARY_LABEL_TOOLTIP_CLASS}
      />
      <span className={SUMMARY_VALUE_CLASS}>{value}</span>
    </div>
  );
}

const CONTACT_NOTICE_BASE_CLASS =
  "rounded border px-3 py-2 font-sans text-sm leading-snug";

const CONTACT_NOTICE_VARIANTS = {
  ok: `${CONTACT_NOTICE_BASE_CLASS} border-emerald-200 bg-emerald-50 text-emerald-950`,
  warn: `${CONTACT_NOTICE_BASE_CLASS} border-amber-200 bg-amber-50 text-amber-950`,
  neutral: `${CONTACT_NOTICE_BASE_CLASS} border-gray-200 bg-gray-50 text-gray-font`,
} as const;

type ContactNoticeProps = {
  tone: keyof typeof CONTACT_NOTICE_VARIANTS;
  children: React.ReactNode;
};

function ContactNotice({
  tone,
  children,
}: ContactNoticeProps): React.ReactElement {
  return <div className={CONTACT_NOTICE_VARIANTS[tone]}>{children}</div>;
}

export function AccountingFirmContactNotices({
  validation,
  inheritContactsNotice,
}: {
  validation: FirmContactValidation;
  inheritContactsNotice?: string | null;
}): React.ReactElement {
  const primaryLabel = validation.primaryContactName || validation.primaryContactId;
  const accountingLabel =
    validation.accountingContactName || validation.accountingContactId;

  return (
    <div className="flex flex-col gap-2">
      {inheritContactsNotice ? (
        <ContactNotice tone="warn">{inheritContactsNotice}</ContactNotice>
      ) : null}

      <ContactNotice tone={validation.hasPrimaryContact ? "ok" : "warn"}>
        {validation.hasPrimaryContact
          ? `Primary contact assigned${primaryLabel ? `: ${primaryLabel}` : ""}.`
          : "No primary contact assigned."}
      </ContactNotice>

      <ContactNotice tone={validation.hasAccountingContact ? "ok" : "warn"}>
        {validation.hasAccountingContact
          ? `Accounting contact defined${accountingLabel ? `: ${accountingLabel}` : ""}.`
          : "No accounting contact defined."}
      </ContactNotice>

      <ContactNotice
        tone={
          !validation.canComparePrimaryAndAccounting
            ? "neutral"
            : validation.primaryAndAccountingSamePerson
              ? "warn"
              : "ok"
        }
      >
        {!validation.canComparePrimaryAndAccounting
          ? "Primary and accounting contacts cannot be compared until both are assigned."
          : validation.primaryAndAccountingSamePerson
            ? `Primary and accounting contact are the same person${
                primaryLabel ? `: ${primaryLabel}` : ""
              }.`
            : "Primary and accounting contact are different people."}
      </ContactNotice>
    </div>
  );
}

export function AccountingPanelSubsection({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="mt-6 border-t border-gray-200 pt-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className={SECTION_TITLE_CLASS.replace("mb-4", "mb-0")}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

export function AccountingInsetPanel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div className={`${ACCOUNTING_INSET_PANEL_CLASS} ${className}`.trim()}>
      {children}
    </div>
  );
}
